import { debounce, cloneDeep, get, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import promotionTabs from '@enums/promotion-tabs';
import attributeFilterValueSelectionModes from '@enums/attribute-filter-value-selection-modes';

const { offer } = promotionTabs;
const { INCLUDE } = attributeFilterValueSelectionModes;

const emptyAttribute = {
    attributeKey: null,
    attributeName: null,
    values: [],
    mode: INCLUDE,
};

const createEmptyAttribute = () => ({ ...emptyAttribute, key: uuidv4() });

const createAttributeSegment = () => ({
    id: uuidv4(),
    key: uuidv4(),
    label: null,
    rows: [createEmptyAttribute()],
});

const isMultipleAttributeSegmentsEnabled = context =>
    context.isMultipleAttributeSegmentsEnabled ??
    get(context.toggleLogic, 'enableMultipleAttributeSegments', false);

export function initializeAttributeFilterState(context) {
    const { stagingAreaPromotion } = context;

    if (!Array.isArray(stagingAreaPromotion.attributeScope)) {
        stagingAreaPromotion.attributeScope = [];
    }

    if (!stagingAreaPromotion.attributeScope.length) {
        stagingAreaPromotion.attributeScope.push({
            ...createEmptyAttribute(),
            rangeOperator: null,
            minValue: null,
            maxValue: null,
        });
    }

    if (
        isMultipleAttributeSegmentsEnabled(context) &&
        !(stagingAreaPromotion.attributeSegments || []).length
    ) {
        stagingAreaPromotion.attributeSegments = [
            {
                id: uuidv4(),
                key: uuidv4(),
                label: null,
                rows: cloneDeep(stagingAreaPromotion.attributeScope),
            },
        ];
    }
}

/**
 * Adds a new attribute segment to the staging area promotion.
 * @param {Object} context - Component context or state object
 */
export function addSegment(context) {
    const { stagingAreaPromotion, selectedCategoryKeys, namespace } = context;

    stagingAreaPromotion.attributeSegments = [
        ...(stagingAreaPromotion.attributeSegments || []),
        createAttributeSegment(),
    ];
    context.userInteraction = true;
    context.setUnsavedPromotion({ namespace, tab: offer, value: true });

    if (selectedCategoryKeys.length) {
        context.debouncedOnProductsNumberUpdated({ markDirty: true });
    }
}

/**
 * Removes an attribute segment from the staging area promotion.
 * @param {Object} context - Component context or state object
 * @param {number} segmentIndex - Segment index to remove
 */
export function removeSegment(context, segmentIndex) {
    const { stagingAreaPromotion, namespace } = context;
    const segments = stagingAreaPromotion.attributeSegments || [];

    if (segments.length <= 1) {
        stagingAreaPromotion.attributeSegments = [createAttributeSegment()];
    } else {
        (segments[segmentIndex].rows || []).forEach(row => {
            delete context.attributeFilterAttributeKeysByRowKey[row.key];
            delete context.attributeFilterValueOptionsByRowKey[row.key];
        });
        stagingAreaPromotion.attributeSegments = segments.filter(
            (_, index) => index !== segmentIndex
        );
    }

    context.userInteraction = true;
    context.setUnsavedPromotion({ namespace, tab: offer, value: true });
    context.debouncedOnProductsNumberUpdated({ markDirty: true });
}

/**
 * Adds a new attribute row to the staging area promotion.
 * @param {Object} context - Component context or state object
 * @param {Object} options - Options object with optional segmentIndex
 */
export function addAttributeRow(context, { segmentIndex = null } = {}) {
    const { attributeScope, stagingAreaPromotion, selectedCategoryKeys, namespace } = context;
    const newAttributes = [createEmptyAttribute()];

    if (isMultipleAttributeSegmentsEnabled(context) && !isNil(segmentIndex)) {
        stagingAreaPromotion.attributeSegments[segmentIndex].rows = [
            ...stagingAreaPromotion.attributeSegments[segmentIndex].rows,
            ...newAttributes,
        ];
        context.userInteraction = true;
        context.setUnsavedPromotion({ namespace, tab: offer, value: true });

        if (selectedCategoryKeys.length) {
            context.debouncedOnProductsNumberUpdated({ markDirty: true });
        }
        return;
    }

    if (!attributeScope.length) {
        newAttributes.push(createEmptyAttribute());
    }

    stagingAreaPromotion.attributeScope = [...attributeScope, ...newAttributes];
    context.userInteraction = true;
    context.setUnsavedPromotion({ namespace, tab: offer, value: true });

    if (selectedCategoryKeys.length) {
        context.debouncedOnProductsNumberUpdated({ markDirty: true });
    }
}

/**
 * Removes an attribute row from the staging area promotion.
 * @param {Object} context - Component context or state object
 * @param {Object} event - Event object with index, key, and optional segmentIndex
 */
export function onRemoveAttributeRow(context, event) {
    const { index, key, segmentIndex = null } = event;
    const { stagingAreaPromotion, selectedCategoryKeys, namespace } = context;

    context.userInteraction = true;
    context.setUnsavedPromotion({ namespace, tab: offer, value: true });

    if (isMultipleAttributeSegmentsEnabled(context) && !isNil(segmentIndex)) {
        const segmentRows = stagingAreaPromotion.attributeSegments[segmentIndex].rows;

        if (segmentRows.length <= 1) {
            stagingAreaPromotion.attributeSegments[segmentIndex].rows = [createEmptyAttribute()];
        } else {
            if (key) {
                delete context.attributeFilterAttributeKeysByRowKey[key];
                delete context.attributeFilterValueOptionsByRowKey[key];
            }
            segmentRows.splice(index, 1);
        }

        context.debouncedOnProductsNumberUpdated({ markDirty: true });
        return;
    }

    if (stagingAreaPromotion.attributeScope.length <= 1) {
        stagingAreaPromotion.attributeScope = [createEmptyAttribute()];

        if (selectedCategoryKeys.length) {
            context.debouncedOnProductsNumberUpdated({ markDirty: true });
        } else {
            context.attributeFilterAttributeKeysByRowKey = {};
            context.attributeFilterValueOptionsByRowKey = {};
            context.productsNumberInPromotion = 0;
        }
        return;
    }

    if (key) {
        delete context.attributeFilterAttributeKeysByRowKey[key];
        delete context.attributeFilterValueOptionsByRowKey[key];
    }

    stagingAreaPromotion.attributeScope.splice(index, 1);
    context.debouncedOnProductsNumberUpdated({ markDirty: true });
}

/**
 * Handles the products number update based on attribute filter options.
 * @param {Object} context - Component context or state object
 * @param {Object} options - Options object with optional markDirty flag
 */
export async function onProductsNumberUpdated(
    context,
    { markDirty = false, orderedSelections = null } = {}
) {
    const {
        selectedCategoryKeys,
        stagingAreaPromotion,
        namespace,
        fetchPromotionAttributeFilterOptions,
        applyPromotionAttributeFilterResponse,
        productOfferGroupId,
    } = context;
    const isDynamicAttributeFilteringEnabled =
        context.isDynamicAttributeFilteringEnabled ??
        get(context.toggleLogic, 'useDynamicAttributeFiltering', false);

    if (markDirty) {
        context.userInteraction = true;
        context.setUnsavedPromotion({ namespace, tab: offer, value: true });
    }

    if (!selectedCategoryKeys.length || !isDynamicAttributeFilteringEnabled) {
        context.productsNumberInPromotion = 0;
        context.attributeFilterAttributeKeysByRowKey = {};
        context.attributeFilterValueOptionsByRowKey = {};
        return;
    }

    context.getProductsInProgress = true;
    const requestId = uuidv4();
    context.getProductsRequestId = requestId;

    const shouldUseSegments = isMultipleAttributeSegmentsEnabled(context);
    const selections = orderedSelections || stagingAreaPromotion.attributeScope;
    const segments = orderedSelections || stagingAreaPromotion.attributeSegments;
    const requestPayload = {
        categories: selectedCategoryKeys,
        orderedSelections: shouldUseSegments ? [] : selections,
        requestId,
        scopeToken: `${namespace}:${productOfferGroupId}`,
    };

    if (shouldUseSegments) {
        requestPayload.segments = segments;
    }

    const result = await fetchPromotionAttributeFilterOptions(requestPayload);

    if (
        result.requestId === context.getProductsRequestId &&
        !result.cancelled &&
        !result.ignored &&
        !result.error &&
        result.data
    ) {
        const appliedResponse = await applyPromotionAttributeFilterResponse({
            attributeScope: shouldUseSegments ? [] : stagingAreaPromotion.attributeScope,
            attributeSegments: shouldUseSegments ? stagingAreaPromotion.attributeSegments : [],
            response: result.data,
        });

        context.attributeFilterAttributeKeysByRowKey =
            appliedResponse.availableAttributeKeysByRowKey;
        context.attributeFilterValueOptionsByRowKey = appliedResponse.availableValuesByRowKey;
        context.productsNumberInPromotion = appliedResponse.scopedProductCount;
    }

    if (requestId === context.getProductsRequestId) {
        context.getProductsInProgress = false;
    }
}

/**
 * Creates a debounced version of onProductsNumberUpdated.
 * @param {Function} onProductsNumberUpdatedFn - The onProductsNumberUpdated function
 * @returns {Function} Debounced version of the function
 */
export function createDebouncedOnProductsNumberUpdated(onProductsNumberUpdatedFn) {
    return debounce(async function(options = {}) {
        await onProductsNumberUpdatedFn(this, options);
    }, 500);
}

/**
 * Reverts the attribute scope to its cached state.
 * @param {Object} context - Component context or state object
 */
export function revertAttributeScope(context) {
    const {
        cacheAttributeScope,
        cacheAttributeSegments,
        stagingAreaPromotion,
        selectedCategoryKeys = [],
        numFiltersApplied = 0,
        namespace,
    } = context;

    if (isMultipleAttributeSegmentsEnabled(context)) {
        stagingAreaPromotion.attributeSegments = cacheAttributeSegments
            ? cloneDeep(cacheAttributeSegments)
            : [createAttributeSegment()];
    }

    if (cacheAttributeScope) {
        stagingAreaPromotion.attributeScope = cloneDeep(cacheAttributeScope);
    } else {
        stagingAreaPromotion.attributeScope = [createEmptyAttribute()];
    }

    context.attributeFilterAttributeKeysByRowKey = {};
    context.attributeFilterValueOptionsByRowKey = {};

    if (selectedCategoryKeys.length || numFiltersApplied > 0) {
        context.debouncedOnProductsNumberUpdated();
    } else {
        context.productsNumberInPromotion = stagingAreaPromotion.products.length;
    }

    context.userInteraction = false;
    context.setUnsavedPromotion({ namespace, tab: 'all', value: false });
}
